import { getAppInsights } from "./applicationInsights";
import { isLocal, isProduction } from "./serviceConfig";

const MAX_BREADCRUMBS = 30;
const ERROR_THROTTLE_MS = 60_000;

const breadcrumbs = [];
const errorCache = new Map();

const getErrorMessage = (error) => {
    if (!error) return "Unknown error";
    if (typeof error === "string") return error;

    return (
        error.message
        || error.reason?.message
        || error.toString?.()
        || "Unknown error"
    );
};

const getErrorStack = (error) => {
    if (!error) return undefined;

    return error.stack || error.reason?.stack;
};

const getContext = () => ({
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    breadcrumbs: [...breadcrumbs],
});

const shouldSkipRepeatedError = (message, pathname, source) => {
    const key = `${source}:${pathname}:${message}`;
    const now = Date.now();
    const lastSeen = errorCache.get(key);

    if (lastSeen && now - lastSeen < ERROR_THROTTLE_MS) {
        return true;
    }

    errorCache.set(key, now);

    return false;
};

export const appTrace = {
    breadcrumb(name, data = {}) {
        breadcrumbs.push({
            name,
            data,
            pathname: window.location.pathname,
            timestamp: new Date().toISOString(),
        });

        if (breadcrumbs.length > MAX_BREADCRUMBS) {
            breadcrumbs.shift();
        }

        if (import.meta.env.DEV) {
            console.debug(`[breadcrumb] ${name}`, data);
        }
    },

    event(name, properties = {}) {
        const context = getContext();

        const payload = {
            ...properties,
            pathname: context.pathname,
            url: context.url,
            timestamp: context.timestamp,
        };

        if (!isProduction) {
            console.info(`[AppEvent] ${name}`, payload);
            return;
        }

        getAppInsights()?.trackEvent({
            name,
            properties: payload,
        });
    },

    error(error, extra = {}) {
        const message = getErrorMessage(error);
        const context = getContext();
        const source = extra.source || "application";

        if (shouldSkipRepeatedError(message, context.pathname, source)) {
            return;
        }

        const properties = {
            ...extra,
            source,
            message,
            stack: getErrorStack(error),
            url: context.url,
            pathname: context.pathname,
            search: context.search,
            hash: context.hash,
            userAgent: context.userAgent,
            breadcrumbs: JSON.stringify(context.breadcrumbs),
        };

        if (!isProduction) {
            console.error("[AppError]", {
                message,
                error,
                properties,
            });
        }

        getAppInsights()?.trackException({
            exception: error instanceof Error ? error : new Error(message),
            properties,
        });
    },
};
